export const tutorialGermanLocalization = {
  page: {
    start: {
      title: 'Start – Alles auf einen Blick',
      summary:
        'Deine Startseite bündelt das Wichtigste aus deinem Ort und deiner Region.',
      features: {
        posts: 'Neueste Beiträge aus deinem Ort',
        region: 'Aktuelles aus deiner Region',
        events: 'Anstehende Events aus deinem Ort und deiner Region',
        hints: 'Warnmeldungen und Hinweise zu deinem Profil'
      },
      mock: { header: 'Dein Heimatort' }
    },
    funkzentrale: {
      title: 'Mein Ort – Die Funkzentrale',
      summary:
        'Alles, was in deinem Ort passiert. Und du kannst selbst mitfunken.',
      features: {
        news: 'News aus Rathaus und Gemeinde',
        plausch: 'Plausch: Unterhalte dich mit deinen Nachbarn',
        markt: 'Markt: Verschenken, tauschen, suchen',
        events: 'Events in deiner Nähe',
        create: 'Mit dem Plus-Button eigene Beiträge erstellen'
      }
    },
    gruppenInfos: {
      title: 'Gruppen, Chats & Infos',
      summary:
        'Über die Kanäle oben in „Mein Ort" wechselst du zwischen Funkzentrale, Gruppen & Chats und Infos.',
      features: {
        groups: 'Gruppen zu deinen Themen finden oder gründen',
        chats: 'Persönliche Chats mit anderen',
        sagsUns: 'Sag’s uns: Anliegen direkt an deine Kommune',
        infos: 'Infos: Kommune, Vereine, Ehrenamt, Kitas + Schulen, Religion'
      }
    },
    region: {
      title: 'Meine Region – Blick über den Ortsrand',
      summary: 'Was in den Nachbarorten, im Landkreis und im Land passiert.',
      features: {
        ministries: 'Ministerien: Meldungen aus dem Land',
        regionalRadio: 'Regionalfunk: Beiträge aus dem Umland',
        emergency: 'Blaulicht: Polizei, Feuerwehr und Rettungsdienste',
        events: 'Events im Umkreis – den Radius wählst du selbst'
      },
      mock: { header: 'Deine Region' }
    },
    partner: {
      title: 'Regionale Partner entdecken',
      summary:
        'Landkreise, Verbände und Unternehmen, die die App in deiner Region unterstützen, findest du an einer Stelle.',
      features: {
        open: 'Tippe in „Meine Region" oben auf den Namen deiner Region',
        list: 'Es öffnet sich die Liste aller Partner deiner Region',
        detail: 'Antippen zeigt das Profil mit Kontakt und Angeboten'
      },
      hint: 'Deine Organisation fehlt? In der Partnerliste erfährst du, wie ihr mitmachen könnt.',
      mock: { subtitle: 'Landkreis' }
    },
    profil: {
      title: 'Profil – Deine Einstellungen',
      summary: 'Hier stellst du die App so ein, wie sie zu dir passt.',
      features: {
        edit: 'Profil und Heimatort bearbeiten',
        radius: 'Meine Region & Radius festlegen',
        notifications: 'Benachrichtigungen einstellen',
        tour: 'Diese Tour findest du jederzeit unter „Erste Schritte"'
      }
    },
    rate: {
      title: 'Gefällt dir StadtLand.Funk?',
      summary:
        'Eine Bewertung im App Store oder bei Google Play hilft uns, die App für alle besser zu machen.',
      features: {
        rate: 'Bewerte die App in wenigen Sekunden',
        feedback: 'Schreib uns, was dir gefällt und was noch fehlt',
        invite: 'Empfiehl die App Freunden und Nachbarn'
      },
      mock: { header: 'StadtLand.Funk' },
      action: 'Jetzt bewerten',
      actionIos: 'Im App Store bewerten',
      actionAndroid: 'Bei Google Play bewerten'
    }
  },
  next: 'Weiter',
  close: 'Alles klar'
};

// Guarantees that both translations have the same structure
type TutorialLocalization = typeof tutorialGermanLocalization;

export const tutorialEnglishLocalization: TutorialLocalization = {
  page: {
    start: {
      title: 'Start – Everything at a glance',
      summary:
        'Your start page gathers the essentials from your place and your region.',
      features: {
        posts: 'Latest posts from your place',
        region: 'News from your region',
        events: 'Upcoming events from your place and your region',
        hints: 'Warnings and notes about your profile'
      },
      mock: { header: 'Your home town' }
    },
    funkzentrale: {
      title: 'My Place – The Local Hub',
      summary: 'Everything that happens in your place. And you can join in.',
      features: {
        news: 'News from the town hall and the community',
        plausch: 'Chat: Talk to your neighbours',
        markt: 'Market: Give away, swap, search',
        events: 'Events near you',
        create: 'Create your own posts with the plus button'
      }
    },
    gruppenInfos: {
      title: 'Groups, chats & Community',
      summary:
        'The channels at the top of "My Place" switch between Local Hub, groups & chats and Community.',
      features: {
        groups: 'Find or found groups on your topics',
        chats: 'Personal chats with others',
        sagsUns: 'Tell us: Send concerns straight to your municipality',
        infos: 'Community: municipality, clubs, volunteering, schools, religion'
      }
    },
    region: {
      title: 'My Region – Beyond the village boundary',
      summary:
        'What happens in neighbouring places, the district and the state.',
      features: {
        ministries: 'Ministries: Announcements from the state',
        regionalRadio: 'Regional radio: Posts from the surrounding area',
        emergency: 'Public Safety: Police, fire brigade and rescue',
        events: 'Events nearby – you choose the radius'
      },
      mock: { header: 'Your region' }
    },
    partner: {
      title: 'Discover regional partners',
      summary:
        'Districts, associations and companies supporting the app in your region, all in one place.',
      features: {
        open: 'In "My Region", tap the name of your region at the top',
        list: 'The list of all partners in your region opens',
        detail: 'Tap a partner to see its profile with contact and offers'
      },
      hint: 'Your organisation is missing? The partner list explains how to join.',
      mock: { subtitle: 'District' }
    },
    profil: {
      title: 'Profile – Your settings',
      summary: 'Set up the app the way it suits you.',
      features: {
        edit: 'Edit your profile and home town',
        radius: 'Set My Region & radius',
        notifications: 'Configure notifications',
        tour: 'Find this tour any time under "First steps"'
      }
    },
    rate: {
      title: 'Enjoying StadtLand.Funk?',
      summary:
        'A rating on the App Store or Google Play helps us make the app better for everyone.',
      features: {
        rate: 'Rate the app in a few seconds',
        feedback: 'Tell us what you like and what is missing',
        invite: 'Recommend the app to friends and neighbours'
      },
      mock: { header: 'StadtLand.Funk' },
      action: 'Rate now',
      actionIos: 'Rate on the App Store',
      actionAndroid: 'Rate on Google Play'
    }
  },
  next: 'Next',
  close: 'All right'
};
